import { format, isThisYear, isToday } from 'date-fns';
import { BadgeTone } from '../../components/Styled/Badge';
import { SupportTicket } from '../../graphql/graphql';

// What the status filter can be set to. `snoozed` is an open ticket with a
// snooze date in the future, `all` is every status.
export type StatusFilter =
  | 'open'
  | 'snoozed'
  | 'waiting_user'
  | 'resolved'
  | 'spam'
  | 'archived'
  | 'all';

const STATUS_META: Record<string, { label: string; tone: BadgeTone }> = {
  open: { label: 'Open', tone: 'blue' },
  snoozed: { label: 'Snoozed', tone: 'purple' },
  waiting_user: { label: 'Waiting on user', tone: 'orange' },
  resolved: { label: 'Resolved', tone: 'green' },
  spam: { label: 'Spam', tone: 'red' },
  archived: { label: 'Archived', tone: 'neutral' },
};

const SOURCE_LABELS: Record<string, string> = {
  email: 'Email',
  app_contact: 'Contact form',
  app_report: 'Report',
  app_chat: 'App chat',
  app_live_application: 'Live application',
  system: 'System',
};

export const SOURCE_OPTIONS = Object.entries(SOURCE_LABELS).map(
  ([value, label]) => ({ value, label })
);

export const isSnoozed = (
  ticket: Pick<SupportTicket, 'status' | 'snoozedUntil'>
): boolean =>
  ticket.status === 'open' &&
  !!ticket.snoozedUntil &&
  new Date(ticket.snoozedUntil).getTime() > Date.now();

export const statusMeta = (
  ticket: Pick<SupportTicket, 'status' | 'snoozedUntil'>
): { label: string; tone: BadgeTone } =>
  STATUS_META[isSnoozed(ticket) ? 'snoozed' : ticket.status] ?? {
    label: ticket.status,
    tone: 'neutral',
  };

export const sourceLabel = (source: string): string =>
  SOURCE_LABELS[source] ?? source;

// "subscription_cancel_refund" -> "Subscription cancel refund"
export const categoryLabel = (category: string): string => {
  const words = category.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// Who the ticket is from, as short as it can be said.
export const senderLabel = (
  ticket: Pick<SupportTicket, 'senderName' | 'senderEmail' | 'user'>
): string =>
  ticket.user?.username || ticket.senderName || ticket.senderEmail || 'Unknown';

export const formatListTime = (value: string): string => {
  const date = new Date(value);
  if (isToday(date)) return format(date, 'HH:mm');
  return format(date, isThisYear(date) ? 'MMM d' : 'MMM d, yyyy');
};

export const formatFullTime = (value: string): string => {
  const date = new Date(value);
  return format(date, isThisYear(date) ? 'MMM d, HH:mm' : 'MMM d yyyy, HH:mm');
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
